import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Building2,
  Clock,
  DollarSign,
  Shield,
  Bus,
  HardHat,
  Lightbulb,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Scale,
  Heart,
  Briefcase,
  XCircle,
  AlertTriangle,
  Eye,
  Handshake,
  TrendingUp,
  Zap,
  CalendarDays,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const sectionNav = [
  { id: "core-message", label: "Core Message" },
  { id: "scheduling", label: "Scheduling" },
  { id: "wages", label: "Wages" },
  { id: "protections", label: "Protections" },
  { id: "time-off", label: "Time Off" },
  { id: "transportation", label: "Transportation" },
  { id: "safety", label: "Safety & Equity" },
  { id: "future", label: "Future Economy" },
];

const faqs = [
  {
    question: "Won't these policies hurt businesses?",
    answer:
      "No. These policies are designed to reward responsible companies and stop exploitation by bad actors. When workers are paid fairly, scheduling is predictable, and conditions are safe, businesses see lower turnover, higher productivity, and stronger consumer spending. The companies that struggle under these rules are the ones profiting from exploitation — and they deserve to be held accountable.",
  },
  {
    question: "Why phase in minimum wage over 10 years?",
    answer:
      "A 10-year phase-in gives businesses time to plan, adjust, and grow. It avoids economic shock while ensuring wages steadily rise to match the real cost of living. This balanced approach protects both workers and employers during the transition.",
  },
  {
    question: "What about small businesses?",
    answer:
      "Small businesses are often the most affected by unfair competition from large companies that cut costs through exploitation. By leveling the playing field, these policies actually help small businesses compete fairly. When all companies play by the same rules, the advantage goes to quality and service — not to whoever can pay workers the least.",
  },
  {
    question: "How does free public transit work?",
    answer:
      "Every Nevada resident gets two free rides per day on public transportation. This is funded through a combination of state investment and the economic benefits of increased workforce participation. When workers can reliably get to their jobs, businesses benefit from lower absenteeism, and the entire economy grows.",
  },
  {
    question: "What is 'piercing the corporate veil'?",
    answer:
      "It means holding individual executives personally accountable when they use corporate structures to shield themselves from consequences of criminal acts — like wage theft, fraud, bribery, abuse, or environmental damage. This targets executives who knowingly commit or authorize crimes, not legitimate business decisions or honest mistakes.",
  },
  {
    question: "Does the 'right to disconnect' mean employers can't contact workers?",
    answer:
      "It means workers cannot be penalized for not responding to work communications outside their scheduled hours. Employers can still send messages — but workers have the right to rest without fear of retaliation. This reduces burnout, improves mental health, and makes workers more productive during actual working hours.",
  },
];

const ProWorkerPage = () => {
  const [showNav, setShowNav] = useState(false);

  return (
    <div data-testid="pro-worker-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Pro-Worker, NOT Anti-Business
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-4xl">
            A Pro-Worker, Pro-Business Plan for Nevada
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            Nevada can lead the nation by protecting workers while
            strengthening companies and economic growth. These goals are not in
            conflict — they are the same goal.
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="#core-message"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <a
              href="#scheduling"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Why This Helps Nevada Companies
            </a>
          </div>

          {/* Two Pillars */}
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
            <div className="bg-white/10 border border-white/20 p-5 flex items-center gap-3">
              <Users className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
              <div>
                <p className="text-white font-bold text-sm">
                  Fairness for Workers
                </p>
                <p className="text-white/60 text-xs">
                  Fair pay, safe conditions, real protections
                </p>
              </div>
            </div>
            <div className="bg-white/10 border border-white/20 p-5 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
              <div>
                <p className="text-white font-bold text-sm">
                  Stability & Growth for Businesses
                </p>
                <p className="text-white/60 text-xs">
                  Lower turnover, higher productivity, fair competition
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION NAV ── */}
      <div className="sticky top-16 md:top-20 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setShowNav(!showNav)}
            className="lg:hidden w-full flex items-center justify-between py-3 text-sm font-bold text-[#002B7F]"
          >
            Jump to Section
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showNav ? "rotate-180" : ""}`}
            />
          </button>
          <nav
            className={`${showNav ? "block" : "hidden"} lg:block overflow-x-auto`}
          >
            <div className="flex items-center gap-1 py-2 min-w-max">
              {sectionNav.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setShowNav(false)}
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[#002B7F] hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* ── CORE MESSAGE ── */}
      <section id="core-message" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Workers and Companies Succeed Together
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Workers are the backbone of Nevada's economy. They build, serve,
                teach, heal, and drive every industry in this state. Without
                them, nothing works.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Businesses create jobs, drive innovation, and fuel growth. They
                are essential to opportunity and prosperity. Nevada needs strong
                companies.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Conflict between workers and businesses is not caused by
                either side doing their job. It is caused by{" "}
                <strong>abuse</strong> — exploitation, wage theft, unsafe
                conditions, and race-to-the-bottom labor practices that hurt
                both workers and responsible employers.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: Handshake,
                  label: "Level the playing field",
                  color: "bg-blue-50 border-blue-200 text-blue-800",
                },
                {
                  icon: CheckCircle,
                  label: "Reward responsible companies",
                  color: "bg-emerald-50 border-emerald-200 text-emerald-800",
                },
                {
                  icon: XCircle,
                  label: "Stop race-to-the-bottom labor practices",
                  color: "bg-red-50 border-red-200 text-red-800",
                },
                {
                  icon: TrendingUp,
                  label: "Stability creates profit",
                  color: "bg-amber-50 border-amber-200 text-amber-800",
                },
                {
                  icon: Scale,
                  label: "Fairness creates growth",
                  color: "bg-violet-50 border-violet-200 text-violet-800",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`flex items-center gap-3 p-4 border ${item.color}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-bold text-sm">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold text-lg">
              "A fair workplace is a productive workplace."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAIR SCHEDULING ── */}
      <section id="scheduling" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Fair Scheduling & Work Hours
              </h2>
              <p className="text-slate-600">
                Predictable schedules help workers plan their lives and help
                businesses plan their operations.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Worker benefits */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">
                What Changes for Workers
              </h3>
              {[
                "2-week advance scheduling requirement",
                "Predictability penalties for last-minute changes",
                "Guaranteed full-time hours for full-time hires",
                "Maximum 3 mandatory blackout days per year",
                "Holiday work beyond that = double time",
                "Overtime pay for salaried employees over 45 hrs/week",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-2 p-3 bg-white border border-slate-200"
                >
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Business benefits */}
            <div className="bg-emerald-50 border border-emerald-200 p-6 h-fit">
              <h3 className="font-bold text-emerald-800 text-sm uppercase tracking-wider mb-4">
                Why This Helps Businesses
              </h3>
              <div className="space-y-3">
                {[
                  "Reduces turnover and training costs",
                  "Improves productivity and morale",
                  "Helps businesses plan labor more efficiently",
                  "Attracts and retains better talent",
                  "Reduces absenteeism and no-shows",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-emerald-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WAGES & TRANSPARENCY ── */}
      <section id="wages" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Wages & Transparency
              </h2>
              <p className="text-slate-600">
                Workers deserve to know what they are worth — and transparency
                helps companies attract and retain the best talent.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            {[
              {
                title: "Wage Theft Becomes a Criminal Offense",
                desc: "Companies that steal from workers will face criminal prosecution — not just a fine they can absorb as a cost of doing business.",
                accent: "border-l-4 border-l-red-500",
              },
              {
                title: "Cost-of-Living Minimum Wage by County",
                desc: "Minimum wage increases tied to the actual cost of living in each county, phased in over 10 years to give businesses time to plan and adjust.",
                accent: "border-l-4 border-l-[#002B7F]",
              },
              {
                title: "Mandatory Salary Disclosure in Job Postings",
                desc: "All job listings must include salary ranges, so companies compete for talent openly and workers can make informed decisions.",
                accent: "border-l-4 border-l-[#D4AF37]",
              },
              {
                title: "Gig Worker Protections",
                desc: "Rideshare, delivery, and gig workers get minimum wage per active hour, plus tips, plus a stipend for vehicle wear and tear.",
                accent: "border-l-4 border-l-emerald-500",
              },
            ].map((item) => (
              <div
                key={item.title}
                className={`bg-[#F8F9FA] p-5 ${item.accent}`}
              >
                <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-5">
            <p className="text-emerald-800 font-bold text-sm">
              Business benefit: Transparency improves hiring efficiency. Fair
              pay reduces churn and recruitment costs. Companies that pay well
              attract better talent and keep it longer.
            </p>
          </div>
        </div>
      </section>

      {/* ── WORKER PROTECTIONS ── */}
      <section id="protections" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Worker Protections & Accountability
              </h2>
              <p className="text-slate-600">
                Targeting bad actors — not legitimate companies. Reducing legal
                gray zones and encouraging ethical corporate behavior.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-3">
                Constructive Dismissal Protections
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                Being forced to quit because of abuse, harassment, or unsafe
                conditions — known as constructive dismissal — will qualify for
                state unemployment benefits. Workers should not be punished for
                leaving intolerable situations.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#002B7F]/10 border border-[#002B7F]/20 px-4 py-2">
                <CheckCircle className="w-4 h-4 text-[#002B7F]" />
                <span className="text-[#002B7F] font-bold text-sm">
                  Safety net for workers in abusive situations
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-3">
                Anti-Retaliation Enforcement
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                Expanded protections for workers who report wage theft,
                harassment, unsafe conditions, or exercise their right to earned
                vacation. Retaliation against whistleblowers carries severe
                penalties.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#002B7F]/10 border border-[#002B7F]/20 px-4 py-2">
                <CheckCircle className="w-4 h-4 text-[#002B7F]" />
                <span className="text-[#002B7F] font-bold text-sm">
                  Workers can report problems without fear
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-3">
                Corporate Accountability — "Piercing the Corporate Veil"
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                Expanded laws to hold corporate executives personally
                accountable for criminal acts: wage theft, fraud, bribery,
                abuse, and environmental damage. No hiding behind corporate
                structures.
              </p>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-red-800 font-bold text-sm">
                  Targets criminal behavior — not legitimate business decisions
                </span>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-5">
            <p className="text-emerald-800 font-bold text-sm">
              Business benefit: Protecting workers protects companies by
              preventing costly lawsuits, turnover, and reputational damage.
              Ethical companies benefit when bad actors are held accountable.
            </p>
          </div>
        </div>
      </section>

      {/* ── TIME OFF ── */}
      <section id="time-off" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <CalendarDays className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Time Off, Vacation & Holidays
              </h2>
              <p className="text-slate-600">
                Rested workers are productive workers. Lower burnout means
                higher retention.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              {[
                {
                  title: "Earned Vacation Must Be Honored",
                  desc: "Workers must receive their earned vacation time, with blackout periods strictly limited",
                },
                {
                  title: "Limited Blackout Days",
                  desc: "Maximum 3 mandatory blackout days per year — no more canceling your family vacation",
                },
                {
                  title: "Double Pay for Holiday Work",
                  desc: "Required holiday work beyond blackout limits must be paid at double pay",
                },
                {
                  title: "10 Paid Sick Days Minimum",
                  desc: "Every worker gets at least 10 paid sick days per year, with carryover allowed",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-[#F8F9FA] border border-slate-200 p-4"
                >
                  <h3 className="font-bold text-slate-900 text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-[#002B7F] p-6 text-white h-fit">
              <h3 className="font-bold mb-4">By the Numbers</h3>
              <div className="space-y-4">
                <div className="border-b border-white/20 pb-4">
                  <p className="text-3xl font-black text-[#D4AF37]">10</p>
                  <p className="text-white/70 text-sm">
                    Minimum paid sick days per year
                  </p>
                </div>
                <div className="border-b border-white/20 pb-4">
                  <p className="text-3xl font-black text-white">3</p>
                  <p className="text-white/70 text-sm">
                    Maximum mandatory blackout days
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-black text-white">2x</p>
                  <p className="text-white/70 text-sm">
                    Pay for holiday work beyond limits
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-5">
            <p className="text-emerald-800 font-bold text-sm">
              Business benefit: Creates loyal, motivated employees. Reduces
              burnout and absenteeism. Companies that respect time off see
              higher productivity and retention.
            </p>
          </div>
        </div>
      </section>

      {/* ── TRANSPORTATION ── */}
      <section id="transportation" className="py-16 md:py-24 bg-[#002B7F] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Bus className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Transportation Access
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Access to work should not be a barrier to opportunity — and
              reliable transportation benefits companies too.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "2 Free Rides Per Day",
                desc: "Every Nevada resident gets two free rides on public transit daily — removing transportation barriers to employment",
              },
              {
                title: "Expanded Statewide Transit",
                desc: "Expanded bus and light rail access across Nevada, connecting workers to opportunity across the state",
              },
              {
                title: "Biking & EV Support",
                desc: "Investment in biking infrastructure, EV charging stations, and alternative commuting options",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/10 border border-white/20 p-5"
              >
                <h3 className="font-bold text-white text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white/10 border border-white/20 p-5 text-center">
            <p className="text-[#D4AF37] font-bold text-sm">
              Business benefit: Workers show up on time, companies run
              efficiently, absenteeism drops, and Nevada's economy grows.
            </p>
          </div>
        </div>
      </section>

      {/* ── SAFETY & EQUITY ── */}
      <section id="safety" className="py-16 md:py-24 bg-white scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <HardHat className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Workplace Safety & Equity
              </h2>
              <p className="text-slate-600">
                Safe, fair workplaces are smart business. Safety reduces
                downtime and liability. Fairness improves morale and retention.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                icon: HardHat,
                title: "Stronger OSHA Enforcement",
                desc: "Increased inspections and significantly higher penalties for unsafe workplaces",
                color: "bg-amber-50 border-amber-200 text-amber-800",
                iconColor: "text-amber-600",
              },
              {
                icon: Lightbulb,
                title: "Whistleblower Protections",
                desc: "Robust state-level protections for workers reporting abuse, fraud, environmental hazards, or unsafe conditions",
                color: "bg-blue-50 border-blue-200 text-blue-800",
                iconColor: "text-blue-600",
              },
              {
                icon: Scale,
                title: "Equal Pay Enforcement",
                desc: "Enforce equal pay laws with real penalties for discrimination based on gender, race, or disability",
                color: "bg-violet-50 border-violet-200 text-violet-800",
                iconColor: "text-violet-600",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`border p-5 ${item.color}`}
                >
                  <Icon className={`w-6 h-6 ${item.iconColor} mb-3`} />
                  <h3 className="font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-sm opacity-80 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-5">
            <p className="text-emerald-800 font-bold text-sm">
              Business benefit: Protecting workers protects companies from
              lawsuits, reputational harm, and turnover — creating a more stable
              and competitive workforce.
            </p>
          </div>
        </div>
      </section>

      {/* ── MODERN WORKFORCE ── */}
      <section id="future" className="py-16 md:py-24 bg-[#F8F9FA] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Modern Workforce & Future Economy
              </h2>
              <p className="text-slate-600">
                Building long-term workforce stability so Nevada can compete
                economically and lead in innovation.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            {[
              {
                icon: Heart,
                title: "Portable Benefits for Gig Workers",
                desc: "Health, retirement, and PTO tied to the worker — not the company. Benefits follow you wherever you work.",
              },
              {
                icon: Lightbulb,
                title: "State-Funded Job Training & Upskilling",
                desc: "State-run worker training and upskilling programs so Nevadans can grow into higher-paying, higher-skilled roles.",
              },
              {
                icon: Building2,
                title: "Tax Incentives for Employee Ownership",
                desc: "Tax incentives for companies offering Employee Stock Ownership Plans (ESOPs) — building worker wealth and company loyalty.",
              },
              {
                icon: Shield,
                title: "Right to Disconnect",
                desc: "Workers cannot be penalized for not responding to work communications outside their scheduled hours. Rest is not optional.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white border border-slate-200 p-5"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm mb-1">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-5">
            <p className="text-emerald-800 font-bold text-sm">
              Business benefit: Builds long-term workforce stability, helps
              Nevada compete economically, and encourages innovation and
              loyalty. Companies with invested workers outperform those without.
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL UNIFYING MESSAGE ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            A Stronger Nevada Starts Here
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 text-left mb-10">
            {[
              "This is pro-worker AND pro-business",
              "Nevada becomes more competitive under these rules",
              "Good employers benefit from a level playing field",
              "Bad actors lose their unfair advantage",
              "Workers get fair pay, safe conditions, and real protections",
              "The goal is a stable, fair, high-growth economy",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 bg-[#F8F9FA] border border-slate-200 p-4"
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-slate-800 font-medium text-sm">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-8">
            <p className="text-white font-bold text-lg leading-relaxed">
              "When workers are protected and companies compete fairly, Nevada
              wins."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Build a Stronger Nevada Together
          </h2>
          <p className="text-white/70 mb-8">
            Workers and businesses succeed together. Join the movement for a
            fair, stable, high-growth Nevada economy.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/health-insurance"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              NV Health Insurance Reform <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProWorkerPage;
